/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";

import { Salary171OtherAllowance } from "../../../../config/filingConstants";
import { IAddIncomeForm } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { ContextAwareToggle } from "../../../accordion";
import { FormField, NumericInputField, SelectInputField } from "../../../fields";

const SalarySection17OneForm: React.FC = () => {
	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();
	const [disableInput, setDisableInput] = useState(false);
	const [initBreakupSum, setInitBreakupSum] = useState(0);

	useEffect(() => {
		handleSetBreakUpValues(Number(values?.SalaryAsPerSection17_1 || 0));
	}, []);

	const handleSetBreakUpValues = (value: number) => {
		if (value) {
			const basicSalary = Math.round(value * 0.5);
			setFieldValue(`SalaryOtherAllowances.${0}[1]`, basicSalary);
			const HRA = Math.round(value * 0.2);
			setFieldValue(`SalaryOtherAllowances.${1}[1]`, HRA);
			const LTA = Math.round(value * 0.041);
			setFieldValue(`SalaryOtherAllowances.${2}[1]`, LTA);
			const otherAllowances = Math.round(value - (basicSalary + HRA + LTA));
			setFieldValue(`SalaryOtherAllowances.${3}[0]`, "Other Allowance");
			setFieldValue(`SalaryOtherAllowances.${3}[1]`, otherAllowances);
		}
		setInitBreakupSum(() => new Date().getTime());
	};

	return (
		<>
			<Accordion className="accordion-sm" defaultActiveKey="gross-salary-1-a">
				<Accordion.Item eventKey="gross-salary-1-a">
					<ContextAwareToggle eventKey="gross-salary-1-a" isChild={true}>
						<div className="btn-content justify-content-between">
							<Form.Label className="leading-para required-field mb-0">
								I(a). Salary as per section 17(1)
								<span
									className="info-wrap"
									role="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										dispatch(
											showHintModal({
												title: "Gross Salary",
												componentName: "GrossSalary171Hint"
											})
										);
									}}
								>
									<FASvgIcon icon={faInfoCircle} />
								</span>
							</Form.Label>
							<div
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
								className="flex-grow-1"
							>
								<NumericInputField
									showLeftIcon={true}
									name="SalaryAsPerSection17_1"
									value={values?.SalaryAsPerSection17_1 || ""}
									onChange={(value) => setFieldValue("SalaryAsPerSection17_1", value)}
									onBlur={() => {
										handleBlur("SalaryAsPerSection17_1");
										if (values?.SalaryAsPerSection17_1) {
											handleSetBreakUpValues(Number(values.SalaryAsPerSection17_1));
										}
									}}
									isInvalid={
										Boolean(errors.SalaryAsPerSection17_1) && touched.SalaryAsPerSection17_1
									}
									errorMsg={errors.SalaryAsPerSection17_1}
									isDisabled={disableInput}
								/>
							</div>
						</div>
					</ContextAwareToggle>

					<Accordion.Body className="pb-0">
						<Section17OneBreakupForm
							handleOnSumChange={(val) => setDisableInput(() => val)}
							initBreakupSum={initBreakupSum}
						/>
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>
		</>
	);
};

export default SalarySection17OneForm;

type FormProps = {
	handleOnSumChange: (isDisabled: boolean) => void;
	initBreakupSum: number;
};

const Section17OneBreakupForm: React.FC<FormProps> = (props: FormProps) => {
	const { handleOnSumChange, initBreakupSum } = props;

	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();

	const [sum, setSum] = useState(0);
	const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

	useEffect(() => {
		let _sum = 0;

		values?.SalaryOtherAllowances?.forEach((item) => {
			if (item.length && item[1]) {
				_sum += Math.round(parseInt(item[1]) || 0);
			}
		});

		setSum(() => _sum);

		// also update the gross total in upper section
		handleOnSumChange(_sum > 0);
		setTimeout(() => {
			setFieldValue("SalaryAsPerSection17_1", _sum || "");
		}, 100);
	}, [currentTime, initBreakupSum]);

	const handleRemoveBreakup = () => {
		setFieldValue(`SalaryOtherAllowances`, [
			["Basic Salary", ""],
			["HRA", ""],
			["LTA", ""]
		]);
		setSum(() => 0);
		handleOnSumChange(false);
		setFieldValue("SalaryAsPerSection17_1", values.SalaryAsPerSection17_1);
	};

	return (
		<>
			<Card className="more-info-card shadow-none">
				<Card.Header>
					<Card.Title>
						Break up of salary as per section 17(1)
						{sum ? (
							<span
								role="button"
								className="text-primary text-decoration-underline"
								onClick={() => handleRemoveBreakup()}
							>
								Remove Breakup
							</span>
						) : null}
					</Card.Title>
				</Card.Header>
				<Card.Body>
					<section>
						<FormField label="Basic Salary" sm={6}>
							<NumericInputField
								showLeftIcon={true}
								name={`SalaryOtherAllowances.${0}[1]`}
								value={values.SalaryOtherAllowances[0][1]}
								onChange={(value) => setFieldValue(`SalaryOtherAllowances.${0}[1]`, value)}
								onBlur={() => {
									handleBlur(`SalaryOtherAllowances.${0}[1]`);
									setCurrentTime(new Date().getTime());
								}}
								isInvalid={
									Boolean(getIn(errors, `SalaryOtherAllowances.${0}[1]`)) &&
									getIn(touched, `SalaryOtherAllowances.${0}[1]`)
								}
								errorMsg={getIn(errors, `SalaryOtherAllowances.${0}[1]`)}
							/>
						</FormField>

						<FormField
							label={
								<>
									House Rent Allowance
									<span
										className="info-wrap"
										role="button"
										onClick={() =>
											dispatch(
												showHintModal({
													title: "HRA Allowance",
													componentName: "HRAAllowanceHint"
												})
											)
										}
									>
										<FASvgIcon icon={faInfoCircle} />
									</span>
								</>
							}
							sm={6}
						>
							<NumericInputField
								showLeftIcon={true}
								name={`SalaryOtherAllowances.${1}[1]`}
								value={values.SalaryOtherAllowances[1][1]}
								onChange={(value) => setFieldValue(`SalaryOtherAllowances.${1}[1]`, value)}
								onBlur={() => {
									handleBlur(`SalaryOtherAllowances.${1}[1]`);
									setCurrentTime(new Date().getTime());
								}}
								isInvalid={
									Boolean(getIn(errors, `SalaryOtherAllowances.${1}[1]`)) &&
									getIn(touched, `SalaryOtherAllowances.${1}[1]`)
								}
								errorMsg={getIn(errors, `SalaryOtherAllowances.${1}[1]`)}
							/>
						</FormField>

						<FormField
							label={
								<>
									LTA
									<span
										className="info-wrap"
										role="button"
										onClick={() =>
											dispatch(
												showHintModal({
													title: "Leave Travel Allowance in Form 16 Annexure",
													componentName: "LTAAllowanceHint"
												})
											)
										}
									>
										<FASvgIcon icon={faInfoCircle} />
									</span>
								</>
							}
							sm={6}
						>
							<NumericInputField
								showLeftIcon={true}
								name={`SalaryOtherAllowances.${2}[1]`}
								value={values.SalaryOtherAllowances[2][1]}
								onChange={(value) => setFieldValue(`SalaryOtherAllowances.${2}[1]`, value)}
								onBlur={() => {
									handleBlur(`SalaryOtherAllowances.${2}[1]`);
									setCurrentTime(new Date().getTime());
								}}
								isInvalid={
									Boolean(getIn(errors, `SalaryOtherAllowances.${2}[1]`)) &&
									getIn(touched, `SalaryOtherAllowances.${2}[1]`)
								}
								errorMsg={getIn(errors, `SalaryOtherAllowances.${2}[1]`)}
							/>
						</FormField>
					</section>
					<section>
						<FieldArray name="SalaryOtherAllowances">
							{({ push, remove, form }) => (
								<>
									{values?.SalaryOtherAllowances?.map((_, index) => {
										if ([0, 1, 2].includes(index)) {
											return null;
										}

										return (
											<section className="mb-3 mb-sm-0" key={`salary-allowance-${index}`}>
												<Salary171Form
													index={index}
													form={form}
													onRemove={(itemIdx: number) => {
														remove(itemIdx);
														setCurrentTime(new Date().getTime());
													}}
													handleAmountInc={() => setCurrentTime(new Date().getTime())}
												/>
											</section>
										);
									})}
									<PreventEditComponent>
										<Button variant="link" className="btn-add-link" onClick={() => push(["", ""])}>
											<FASvgIcon icon={faCirclePlus} />
											Add more
										</Button>
									</PreventEditComponent>
								</>
							)}
						</FieldArray>
					</section>
				</Card.Body>
				<Card.Footer>
					<p className="total-content">
						<span className="total-label">Total:</span>
						<span className="total-amount">
							<PriceDisplay price={sum} />
						</span>
					</p>
				</Card.Footer>
			</Card>
		</>
	);
};

type Props = {
	index: number;
	form: FormikProps<IAddIncomeForm>;
	onRemove: (itemIdx: number) => void;
	handleAmountInc: () => void;
};

const Salary171Form: React.FC<Props> = (props: Props) => {
	const { index, onRemove, form, handleAmountInc } = props;

	const { setFieldValue, handleBlur, values, touched, errors } = form;

	return (
		<>
			<Row className="align-items-center">
				<Col sm={6}>
					<SelectInputField
						placeholder="Select other"
						name={`SalaryOtherAllowances.${index}[0]`}
						isClearable={false}
						isOptionArrayOfObject={false}
						options={Salary171OtherAllowance}
						value={[values.SalaryOtherAllowances[index][0]]}
						onChange={(selectedOption) => {
							if (selectedOption) {
								setFieldValue(`SalaryOtherAllowances.${index}[0]`, selectedOption.value);
							} else {
								setFieldValue(`SalaryOtherAllowances.${index}[0]`, undefined);
							}
						}}
						onBlur={handleBlur(`SalaryOtherAllowances.${index}[0]`)}
						isInvalid={
							Boolean(getIn(errors, `SalaryOtherAllowances.${index}[0]`)) &&
							getIn(touched, `SalaryOtherAllowances.${index}[0]`)
						}
						errorMsg={getIn(errors, `SalaryOtherAllowances.${index}[0]`)}
					/>
				</Col>
				<Col sm>
					<NumericInputField
						showLeftIcon={true}
						placeholder=""
						name={`SalaryOtherAllowances.${index}[1]`}
						value={values.SalaryOtherAllowances[index][1]}
						onChange={(value) => setFieldValue(`SalaryOtherAllowances.${index}[1]`, value)}
						onBlur={() => {
							handleBlur(`SalaryOtherAllowances.${index}[1]`);
							handleAmountInc();
						}}
						isInvalid={
							Boolean(getIn(errors, `SalaryOtherAllowances.${index}[1]`)) &&
							getIn(touched, `SalaryOtherAllowances.${index}[1]`)
						}
						errorMsg={getIn(errors, `SalaryOtherAllowances.${index}[1]`)}
					/>
				</Col>
				<PreventEditComponent>
					<Col sm={"auto"} className="ps-0 align-self-start">
						<Button
							variant="transparent"
							className="text-greyDark icon-only mt-1 d-none d-sm-flex"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
						</Button>
						<Button
							variant="transparent"
							className="btn-delete-full d-sm-none"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
							<span>Delete</span>
						</Button>
					</Col>
				</PreventEditComponent>
			</Row>
			{index !== values.SalaryOtherAllowances.length - 1 && (
				<hr className="d-block d-sm-none my-4" />
			)}
		</>
	);
};
